import { useState } from 'react'
import { fbt } from 'fbtee'
import {
  feedbackBugReportCategory,
  feedbackFeatureRequestCategory,
  feedbackGeneralFeedbackCategory,
  feedbackHelpQuestionCategory,
  feedbackOtherCategory,
  feedbackSendLabel,
} from '@/lib/commonStrings'
import { useAuth } from '@/hooks/useAuth'
import { useSubmitFeedback } from '@/hooks/useSubmitFeedback'
import { useFeedbackAttachments } from '@/hooks/useFeedbackAttachments'
import { classifyError } from '@/lib/errors'
import type { ClassifiedError } from '@/lib/errors'
import { toastError } from '@/lib/toast'
import { track } from '@/lib/analytics'
import { exportLogs, clearLogs } from '@/lib/logger'
import { FeedbackAttachments } from '@/components/FeedbackAttachments'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { InfoIcon } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { FeedbackCategory } from '@/types'

interface FeedbackFormProps {
  errorContext?: ClassifiedError | null
  initialCategory?: FeedbackCategory
  pendingScreenshot?: Promise<Blob | null>
  entryPoint?: 'form' | 'shake'
  onSuccess: () => void
  wrapSubmit?: (fn: () => void) => () => void
  className?: string
  fullWidthSubmit?: boolean
}

export function FeedbackForm({
  errorContext,
  initialCategory,
  pendingScreenshot,
  entryPoint = 'form',
  onSuccess,
  wrapSubmit,
  className,
  fullWidthSubmit,
}: FeedbackFormProps) {
  const { user, isAdmin } = useAuth()
  const submitFeedback = useSubmitFeedback(user?.uid ?? null)
  const [category, setCategory] = useState<FeedbackCategory>(
    errorContext ? 'bug' : initialCategory ?? 'feedback',
  )
  const [message, setMessage] = useState('')
  const [immediateReview, setImmediateReview] = useState(false)
  const { attachments, setAttachments, pending, dismissPending, collect } = useFeedbackAttachments(pendingScreenshot)

  // Fire-and-forget: the write only resolves on the server ack, but it's
  // already queued in the persistent cache, so the dialog closes right away.
  function submit() {
    if (!user || !message.trim()) return
    const rawLogs = category === 'bug' ? exportLogs() : null
    const logs = rawLogs ? rawLogs.slice(-100_000) : null
    const input = {
      category,
      message: message.trim(),
      immediateReview,
      errorCode: errorContext?.code,
      errorEventId: errorContext?.eventId,
      errorMessage: errorContext?.raw.message,
      logs: logs ?? undefined,
    }
    collect()
      .then(files => submitFeedback(input, files))
      .then(({ failedAttachments }) => {
        if (logs) clearLogs()
        if (failedAttachments > 0) {
          toastError(
            fbt(
              "Some photos couldn't be attached",
              'Toast when feedback was sent but one or more attached photos failed to upload',
            ),
            fbt('Your message was still sent.', 'Toast detail when feedback photos failed to upload but the text went through'),
          )
        }
      })
      .catch(err => {
        toastError(
          fbt("Couldn't send feedback", 'Toast error message when feedback submission fails'),
          classifyError(err),
        )
      })
    track('feedback_submitted', {
      kind: category,
      had_error_context: Boolean(errorContext),
      entry_point: entryPoint,
      attachment_count: attachments.length + (pending ? 1 : 0),
    })
    onSuccess()
  }

  const handleSubmit = wrapSubmit ? wrapSubmit(submit) : submit

  return (
    <div className={className}>
      <div className="space-y-3">
        <Select
          value={category}
          onValueChange={v => setCategory(v as FeedbackCategory)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bug">
              {feedbackBugReportCategory()}
            </SelectItem>
            <SelectItem value="feature">
              {feedbackFeatureRequestCategory()}
            </SelectItem>
            <SelectItem value="feedback">
              {feedbackGeneralFeedbackCategory()}
            </SelectItem>
            <SelectItem value="support">
              {feedbackHelpQuestionCategory()}
            </SelectItem>
            <SelectItem value="other">
              {feedbackOtherCategory()}
            </SelectItem>
          </SelectContent>
        </Select>
        <Textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder={fbt(
            'What happened? What were you trying to do?',
            'Placeholder text in the feedback message textarea',
          )}
          rows={5}
          maxLength={5000}
        />
        <FeedbackAttachments
          attachments={attachments}
          onChange={setAttachments}
          pending={pending}
          onDismissPending={dismissPending}
        />
        {category === 'bug' && (
          <p className="text-xs text-muted-foreground">
            <fbt desc="Info note in bug report form telling the user that diagnostic logs will be attached automatically">
              Boot log will be attached automatically.
            </fbt>
          </p>
        )}
        {errorContext && (
          <p className="text-xs text-muted-foreground">
            <fbt desc="Label prefix shown before an error code when error context is attached to feedback">
              Attaching error:
            </fbt>{' '}
            <span className="font-mono">{errorContext.code}</span>
            {errorContext.eventId && (
              <>
                {' · ref '}
                <span className="font-mono">{errorContext.eventId.slice(0, 8)}</span>
              </>
            )}
          </p>
        )}
        {isAdmin && (
          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
            <Switch
              id="immediate-review"
              checked={immediateReview}
              onCheckedChange={setImmediateReview}
            />
            <Label htmlFor="immediate-review" className="cursor-pointer text-sm">
              {fbt('Address immediately', 'Admin-only toggle label to flag feedback for immediate Claude address')}
            </Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="size-3.5 cursor-pointer text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                {fbt(
                  'Sends this feedback to Claude to address immediately.',
                  'Tooltip explaining the Review immediately admin toggle in the feedback dialog',
                )}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
      <Button
        className={fullWidthSubmit ? 'mt-4 w-full' : 'mt-4'}
        onClick={handleSubmit}
        disabled={!message.trim()}
      >
        {feedbackSendLabel()}
      </Button>
    </div>
  )
}
